import { createContext, useContext, useEffect, useReducer, useMemo, useCallback } from 'react';
import { api, getToken, setToken } from '../api.js';

const AuthContext = createContext(null);

const initial = {
  user: null,
  loading: true,
  error: null,
  registrationOpen: false,
};

function reducer(state, action) {
  switch (action.type) {
    case 'set_loading':
      return { ...state, loading: action.value };
    case 'set_user':
      return { ...state, user: action.user, loading: false, error: null };
    case 'set_error':
      return { ...state, error: action.error, loading: false };
    case 'set_status':
      return { ...state, registrationOpen: action.registrationOpen };
    case 'logout':
      return { ...state, user: null, loading: false, error: null };
    default:
      return state;
  }
}

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initial);

  // bootstrap: chi è loggato + status registrazione
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const status = await api.get('/auth/status');
        if (!cancelled) dispatch({ type: 'set_status', registrationOpen: status.registrationOpen });
      } catch {
        // ignore
      }

      if (!getToken()) {
        if (!cancelled) dispatch({ type: 'set_user', user: null });
        return;
      }
      try {
        const { user } = await api.get('/auth/me');
        if (!cancelled) dispatch({ type: 'set_user', user });
      } catch {
        setToken(null);
        if (!cancelled) dispatch({ type: 'set_user', user: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email, password) => {
    dispatch({ type: 'set_loading', value: true });
    try {
      const { token, user } = await api.post('/auth/login', { email, password });
      setToken(token);
      dispatch({ type: 'set_user', user });
      return user;
    } catch (err) {
      dispatch({ type: 'set_error', error: err.message });
      throw err;
    }
  }, []);

  const register = useCallback(async (email, password, name) => {
    dispatch({ type: 'set_loading', value: true });
    try {
      const { token, user } = await api.post('/auth/register', { email, password, name });
      setToken(token);
      dispatch({ type: 'set_user', user });
      return user;
    } catch (err) {
      dispatch({ type: 'set_error', error: err.message });
      throw err;
    }
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    dispatch({ type: 'logout' });
  }, []);

  const value = useMemo(
    () => ({ ...state, login, register, logout }),
    [state, login, register, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve essere usato dentro AuthProvider');
  return ctx;
}
